// SPDX-License-Identifier: AGPL-3.0-or-later
import type {ScrollerHandle} from '@app/features/ui/components/Scroller';
import {isUnfocusedFullyInteractive} from '@app/features/ui/utils/WindowFocusInteractionGuard';
import type React from 'react';
import {useEffect, useRef} from 'react';

interface VideoPoolControl {
	pauseAll: () => void;
	resumeAll: () => void;
}
interface UseWindowFocusVideoControlOptions {
	scrollerRef: React.RefObject<ScrollerHandle | null>;
	videoPool: VideoPoolControl;
	gifAutoPlay?: boolean;
}
export function useWindowFocusVideoControl({
	scrollerRef,
	videoPool,
	gifAutoPlay = true,
}: UseWindowFocusVideoControlOptions): void {
	const poolRef = useRef(videoPool);
	poolRef.current = videoPool;
	const scrollerRefRef = useRef(scrollerRef);
	scrollerRefRef.current = scrollerRef;
	const gifAutoPlayRef = useRef(gifAutoPlay);
	gifAutoPlayRef.current = gifAutoPlay;
	useEffect(() => {
		if (!gifAutoPlay) {
			poolRef.current.pauseAll();
		} else if (!document.hidden && document.hasFocus()) {
			poolRef.current.resumeAll();
		}
	}, [gifAutoPlay]);
	useEffect(() => {
		const handleBlur = () => {
			const node = scrollerRefRef.current.current?.getScrollerNode();
			if (document.activeElement instanceof HTMLElement && node?.contains(document.activeElement)) {
				const scrollTop = node.scrollTop;
				document.activeElement.blur();
				node.scrollTop = scrollTop;
			}
			if (isUnfocusedFullyInteractive() && !document.hidden) return;
			poolRef.current.pauseAll();
		};
		const handleFocus = () => {
			if (gifAutoPlayRef.current) {
				poolRef.current.resumeAll();
			}
		};
		const handleVisibilityChange = () => {
			if (document.hidden) {
				handleBlur();
			} else {
				handleFocus();
			}
		};
		window.addEventListener('blur', handleBlur);
		window.addEventListener('focus', handleFocus);
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => {
			window.removeEventListener('blur', handleBlur);
			window.removeEventListener('focus', handleFocus);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, []);
}
